import { useCallback, useEffect, useState } from 'react'
import { currentPushState, enablePush, reconcilePush, type PushState } from '../lib/push'
import { useSession } from '../state/session'
import { useToast } from '../state/toast'
import { Button } from './ui'

/**
 * The prompt that gets reminders switched on.
 *
 * The whole app is built around scheduled nudges, but a browser will only ask
 * for notification permission from a user gesture — so there has to be
 * something to press. Leaving that solely in the settings screen meant nobody
 * ever found it: the crew installed the app, granted location, and then silently
 * received nothing, while the delivery log filled up with "no device registered".
 *
 * So it lives on the home screen until it is dealt with. On iPhone it first has
 * to explain the Home Screen step, because iOS gives web apps no push at all
 * until they are installed.
 */

const DISMISS_KEY = 'wm-reminders-dismissed'

function isIphone(): boolean {
  return /iP(hone|od)|iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
}

/** Dismissals last the rest of the day, then the prompt comes back. */
function dismissedToday(): boolean {
  return localStorage.getItem(DISMISS_KEY) === new Date().toISOString().slice(0, 10)
}

export function EnableReminders() {
  const { settings } = useSession()
  const toast = useToast()
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [dismissed, setDismissed] = useState(dismissedToday)

  useEffect(() => {
    // Heal any drift between the browser's subscription and the server before
    // deciding what to show — otherwise a rotated key or a cleaned-up server
    // row leaves the UI saying "on" while nothing can be delivered.
    void reconcilePush().finally(() => void currentPushState().then(setState))
  }, [])

  const turnOn = useCallback(async () => {
    setBusy(true)
    try {
      const result = await enablePush()
      setState(result.state)
      if (result.detail) console.warn('push subscribe failed:', result.detail)
      if (result.state === 'subscribed') toast.success('Reminders are on for this phone')
      else if (result.message) toast.warn(result.message)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not turn on reminders')
    } finally {
      setBusy(false)
    }
  }, [toast])

  // Nothing to ask for if the deployment has no push keys, or this device is set up.
  if (!settings.pushConfigured || state === null || state === 'subscribed') return null
  if (state === 'not-configured' || state === 'no-service-worker') return null

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10))
    setDismissed(true)
  }

  // iPhone gives web apps no push until they are on the Home Screen, so the
  // install step has to come first and cannot be skipped.
  const needsInstall = state === 'unsupported' && isIphone() && !isInstalled()

  if (needsInstall) {
    return (
      <section className="card border-l-4 border-l-brand p-4" aria-labelledby="reminders-install">
        <h2 id="reminders-install" className="text-base font-extrabold">
          Add Windmill to your Home Screen
        </h2>
        <p className="mt-1 text-sm text-graphite-2">
          iPhone only sends reminders to apps on your Home Screen. Once it is there, open Windmill
          from the Home Screen icon and you can turn reminders on.
        </p>
        <ol className="mt-3 space-y-1 text-sm">
          <li>
            <span className="label mr-2">1</span>Tap the Share button at the bottom of Safari
          </li>
          <li>
            <span className="label mr-2">2</span>Choose <strong>Add to Home Screen</strong>
          </li>
          <li>
            <span className="label mr-2">3</span>Open Windmill from the new icon
          </li>
        </ol>
      </section>
    )
  }

  if (state === 'unsupported') {
    // A desktop browser or an old phone. Nothing to press, so say so once.
    return dismissed ? null : (
      <section className="card p-4">
        <p className="text-sm">
          <strong>This browser cannot receive reminders.</strong> Open Windmill on your work phone
          to get them.
        </p>
        <Button className="mt-3" onClick={dismiss}>Got it</Button>
      </section>
    )
  }

  if (state === 'denied') {
    return dismissed ? null : (
      <section className="card border-l-4 border-l-alarm p-4" aria-labelledby="reminders-blocked">
        <h2 id="reminders-blocked" className="text-base font-extrabold">Reminders are blocked</h2>
        <p className="mt-1 text-sm text-graphite-2">
          Notifications are turned off for Windmill in your phone's settings. Allow them there, then
          come back and reopen the app.
        </p>
        <Button className="mt-3" onClick={dismiss}>Got it</Button>
      </section>
    )
  }

  if (dismissed) return null

  return (
    <section className="card border-l-4 border-l-brand p-4" aria-labelledby="reminders-off">
      <h2 id="reminders-off" className="text-base font-extrabold">Reminders are off</h2>
      <p className="mt-1 text-sm text-graphite-2">
        Turn them on and this phone will tell you when to clock in, when to post a progress photo,
        and when to clock out.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="primary" loading={busy} onClick={() => void turnOn()}>
          Turn on reminders
        </Button>
        <Button onClick={dismiss}>Not now</Button>
      </div>
    </section>
  )
}
